(() => {
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const easeOutCubic = (value) => 1 - Math.pow(1 - clamp(value), 3);
  const easeInOutCubic = (value) => {
    const progress = clamp(value);
    return progress < 0.5
      ? 4 * progress * progress * progress
      : 1 - Math.pow(-2 * progress + 2, 3) / 2;
  };

  class RuncamInBoxStory extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.cancelPendingWork();
      this.unlockScroll();
      const wasComplete = this.complete;

      this.abortController = new AbortController();
      this.track = this.querySelector('[data-in-box-track]');
      this.stage = this.querySelector('[data-in-box-stage]');
      this.package = this.querySelector('[data-in-box-package]');
      this.cards = Array.from(this.querySelectorAll('[data-in-box-card]'));
      this.started = false;
      this.active = false;
      this.complete = false;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.classList.remove(
        'is-ready',
        'is-measuring',
        'is-unpacked',
        'is-complete',
        'is-auto-active',
        'is-auto-moving',
        'is-auto-finishing'
      );

      if (!this.track || !this.stage || !this.package || this.cards.length === 0) return;
      for (const element of [this.package, ...this.cards]) {
        element.style.removeProperty('opacity');
        element.style.removeProperty('transform');
      }
      this.classList.add('is-ready');
      if (wasComplete || this.reduceMotion.matches) {
        this.completeStory(true);
        return;
      }

      const { signal } = this.abortController;
      window.addEventListener('resize', this.handleResize, { passive: true, signal });
      this.reduceMotion.addEventListener?.('change', this.handleMotionPreference, { signal });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this.stage);
      }

      window.addEventListener('scroll', this.handleAutoTrigger, { passive: true, signal });

      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.measureCards();
        if (this.reduceMotion.matches) {
          this.completeStory(true);
        } else {
          this.handleAutoTrigger();
        }
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.active = false;
      this.cancelPendingWork();
      this.unlockScroll();
    }

    cancelPendingWork() {
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.finishTimer) window.clearTimeout(this.finishTimer);
      if (this.unlockTimer) window.clearTimeout(this.unlockTimer);
      this.frame = 0;
      this.finishTimer = 0;
      this.unlockTimer = 0;
    }

    handleResize = () => {
      if (this.complete || this.active) return;
      this.measureCards();
      this.handleAutoTrigger();
    };

    handleMotionPreference = () => {
      if (!this.reduceMotion.matches) return;
      if (this.complete) {
        this.cancelPendingWork();
        this.unlockScroll();
        return;
      }
      this.completeStory(true);
    };

    handleAutoTrigger = () => {
      if (this.complete || this.started || this.reduceMotion.matches) return;

      const packageRect = this.package.getBoundingClientRect();
      const packageCenter = packageRect.top + packageRect.height / 2;
      const isVisible = packageRect.bottom > 0 && packageRect.top < window.innerHeight;

      if (isVisible && packageCenter <= window.innerHeight * 0.7) {
        this.startAnimation();
      }
    };

    measureCards() {
      if (!this.stage || this.cards.length === 0) return;

      this.classList.add('is-measuring');
      const stageRect = this.stage.getBoundingClientRect();
      const centerX = stageRect.left + stageRect.width / 2;
      const centerY = stageRect.top + stageRect.height / 2;

      this.cardOffsets = this.cards.map((card) => {
        const rect = card.getBoundingClientRect();
        return {
          x: centerX - (rect.left + rect.width / 2),
          y: centerY - (rect.top + rect.height / 2),
        };
      });
      this.classList.remove('is-measuring');
    }

    startAnimation() {
      if (this.started || this.complete) return;

      this.started = true;
      this.active = true;
      this.lockScroll();
      this.classList.add('is-auto-active', 'is-auto-moving');
      this.package.style.opacity = '1';
      this.package.style.transform = 'none';

      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        if (!this.active) return;
        this.measureCards();
        this.cards.forEach((card, index) => {
          const offset = this.cardOffsets?.[index] || { x: 0, y: 0 };
          card.style.opacity = '0';
          card.style.transform = `translate(${offset.x}px, ${offset.y}px) scale(0.76)`;
        });
        this.animateCards(0);
      });
    }

    animateCards(startTime) {
      const packageFadeDuration = 240;
      const accessoryMoveDuration = 700;
      const accessoryStagger = 40;
      const accessoryStartDelay = packageFadeDuration + 20;
      const totalDuration = accessoryStartDelay + Math.max(0, this.cards.length - 1) * accessoryStagger + accessoryMoveDuration;
      const frame = (timestamp) => {
        if (!this.active) return;
        if (!startTime) startTime = timestamp;

        const elapsed = timestamp - startTime;
        const packageProgress = easeOutCubic(elapsed / packageFadeDuration);
        const packageOpacity = 1 - packageProgress;
        this.package.style.opacity = String(packageOpacity);
        this.package.style.transform = `translateY(${-24 * packageProgress}px) scale(${1 + 0.035 * packageProgress})`;

        this.cards.forEach((card, index) => {
          const offset = this.cardOffsets?.[index] || { x: 0, y: 0 };
          const cardStart = accessoryStartDelay + index * accessoryStagger;
          const cardProgress = clamp((elapsed - cardStart) / accessoryMoveDuration);
          const cardMovement = easeInOutCubic(cardProgress);
          const cardOpacity = easeOutCubic(cardProgress / 0.24);
          const remaining = 1 - cardMovement;
          const arc = Math.sin(cardProgress * Math.PI) * (index % 2 === 0 ? -10 : 10);
          const drift = Math.sin(cardProgress * Math.PI * 1.5 + index * 0.6) * 3;
          card.style.opacity = String(cardOpacity);
          card.style.transform = `translate(${offset.x * remaining + arc}px, ${offset.y * remaining + drift}px) scale(${0.76 + 0.24 * cardMovement})`;
        });

        if (elapsed < totalDuration) {
          this.frame = requestAnimationFrame(frame);
          return;
        }

        this.frame = 0;
        this.revealCards();
      };

      this.frame = requestAnimationFrame(frame);
    }

    revealCards() {
      this.classList.remove('is-auto-moving');
      this.classList.add('is-auto-finishing');
      this.package.style.opacity = '0';
      this.package.style.transform = 'none';

      this.cards.forEach((card) => {
        card.style.opacity = '1';
        card.style.transform = 'none';
      });

      this.finishTimer = window.setTimeout(() => {
        this.finishTimer = 0;
        this.completeStory(false);
      }, 200);
    }

    completeStory(skipAnimation = false) {
      if (this.complete) return;

      this.cancelPendingWork();
      this.complete = true;
      this.active = false;
      this.classList.add('is-complete', 'is-unpacked');
      this.classList.remove(
        'is-auto-active',
        'is-auto-moving',
        'is-auto-finishing'
      );

      this.package.style.removeProperty('opacity');
      this.package.style.removeProperty('transform');
      this.cards.forEach((card) => {
        card.style.removeProperty('opacity');
        card.style.removeProperty('transform');
      });

      window.removeEventListener('scroll', this.handleAutoTrigger);

      if (skipAnimation) {
        this.unlockScroll();
        return;
      }

      this.unlockTimer = window.setTimeout(() => {
        this.unlockTimer = 0;
        this.unlockScroll();
      }, 100);
    }

    lockScroll() {
      if (this.scrollLockController) return;
      this.scrollLockController = new AbortController();
      const { signal } = this.scrollLockController;
      // 仅拦截滚动输入，避免改变吸顶元素依赖的滚动容器。
      const preventScroll = (event) => event.preventDefault();
      document.addEventListener('wheel', preventScroll, { passive: false, capture: true, signal });
      document.addEventListener('touchmove', preventScroll, { passive: false, capture: true, signal });
    }

    unlockScroll() {
      this.scrollLockController?.abort();
      this.scrollLockController = null;
    }

  }

  if (!customElements.get('runcam-in-box-story')) {
    customElements.define('runcam-in-box-story', RuncamInBoxStory);
  }
})();
